/**
 * Menu Registry - Central registry for menu management
 * Created: 1405/02/17
 */

import { MenuItem, MenuConfiguration, MergedMenuItem } from './types';
import { buildMenuTree, flattenMenuTree } from './utils/menu-builder';

// Default configuration ID for runtime override
export const RUNTIME_CONFIG_ID = 'runtime-menu-config';

// In-memory cache for runtime configuration (populated from API)
let runtimeConfigCache: MenuConfiguration | null = null;

// Set runtime configuration (called after fetching from API)
export const setRuntimeConfig = (config: MenuConfiguration | null): void => {
  runtimeConfigCache = config;
};

// Get runtime configuration from cache
export const getRuntimeConfig = (): MenuConfiguration | null => {
  return runtimeConfigCache;
};

// Clear runtime cache
export const clearRuntimeCache = (): void => {
  runtimeConfigCache = null;
};

// Check if runtime config is active and valid
export const isRuntimeConfigActive = (): boolean => {
  return runtimeConfigCache !== null &&
         runtimeConfigCache.isActive &&
         Array.isArray(runtimeConfigCache.items) &&
         runtimeConfigCache.items.length > 0;
};

// Get menu — منوی رانتایم تنها منبع است (base-menu حذف شد)
export const getMergedMenu = (): MenuItem[] => {
  if (isRuntimeConfigActive()) {
    return runtimeConfigCache!.items.filter(item => item.isActive);
  }

  return [];
};

// Get menu tree for sidebar
export const getMenuTree = (): MergedMenuItem[] => {
  const merged = getMergedMenu();
  return buildMenuTree(merged);
};

// Get menu item by ID
export const getMenuItemById = (id: string): MenuItem | undefined => {
  const merged = getMergedMenu();
  return merged.find(item => item.id === id);
};

// Get children of a menu item
export const getMenuChildren = (parentId: string | null): MenuItem[] => {
  const merged = getMergedMenu();
  return merged.filter(item => item.parentId === parentId).sort((a, b) => a.order - b.order);
};

// Get next available order for a parent
export const getNextOrder = (parentId: string | null): number => {
  const siblings = getMenuChildren(parentId);
  if (siblings.length === 0) return 1;
  return Math.max(...siblings.map(s => s.order)) + 1;
};

// Generate unique ID from title
export const generateMenuId = (title: string, parentId?: string | null): string => {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9\u0600-\u06FF]/g, '-') // Allow Persian chars
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  
  const prefix = parentId ? `${parentId}-` : '';
  let id = `${prefix}${base}`;
  
  // Ensure uniqueness
  let counter = 1;
  const merged = getMergedMenu();
  while (merged.some(item => item.id === id)) {
    id = `${prefix}${base}-${counter}`;
    counter++;
  }
  
  return id;
};

// Export all for convenience
export default {
  getMergedMenu,
  getMenuTree,
  getMenuItemById,
  getMenuChildren,
  getNextOrder,
  generateMenuId,
  setRuntimeConfig,
  getRuntimeConfig,
  isRuntimeConfigActive,
  clearRuntimeCache,
  RUNTIME_CONFIG_ID
};
